import { HttpLink, CombinedGraphQLErrors, ServerError } from "@apollo/client";
import type { ApolloLink } from "@apollo/client/link";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import * as Sentry from "@sentry/react";
import { userManager } from "@/auth/auth";

const httpLink = new HttpLink({
  uri: "/graphql",
  headers: { "Accept-Encoding": "br;q=1.0, gzip;q=0.8, *;q=0.1" },
});

// Downstream of the error link, so a blip is retried before it is treated as a
// session to re-establish. Apollo's default retries every error, including the
// 401 whose sign-in that would delay.
const retryLink = new RetryLink({
  attempts: {
    // A 4xx is the API's decision and will not change; anything else might.
    retryIf: (error) =>
      ServerError.is(error) ? error.statusCode >= 500 : true,
  },
});

const authLink = setContext(async () => {
  const user = await userManager.getUser();
  return {
    headers: { authorization: `Bearer ${user?.access_token}` },
  };
});

/** Signing in is per link: the redirect reloads the page, taking it with it. */
export function createLink(): ApolloLink {
  let signinStarted = false;

  const errorLink = new ErrorLink(({ error }) => {
    if (ServerError.is(error)) {
      if (error.statusCode === 401) {
        if (!signinStarted) {
          signinStarted = true;
          void userManager.signinRedirect();
        }
        return;
      }
      // Routine denials are GraphQL errors from PermissionGuard, so reaching
      // here means Keycloak grants this caller nothing and someone must fix it.
      if (error.statusCode === 403) {
        Sentry.captureMessage("Caller has no permissions", "warning");
        return;
      }
      // A 5xx carries no GraphQL body, so there is nothing here to report but
      // the status the caller already has.
      if (error.statusCode >= 500) return;
    }
    const errors = CombinedGraphQLErrors.is(error) ? error.errors : [error];
    for (const err of errors) {
      console.error(err);
      Sentry.captureMessage(`GraphQL error: ${err.message}`, "error");
    }
  });

  return authLink.concat(errorLink).concat(retryLink).concat(httpLink);
}

export const link = createLink();
